import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert
} from '@mui/material';
import {
  Dashboard,
  Link as Integration,
  Settings,
  Assessment,
  Sync,
  Schedule,
  Notifications,
  HealthAndSafety,
  BugReport
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();

  const dashboardCards = [
    {
      title: 'Dashboard Integrazione',
      description: 'Monitora lo stato dell\'integrazione HealthNET-RECUP in tempo reale',
      icon: <Integration color="primary" />,
      path: '/integration',
      color: 'primary'
    },
    {
      title: 'Gestione Prenotazioni',
      description: 'Visualizza e gestisci le prenotazioni sincronizzate',
      icon: <Schedule color="success" />,
      path: '/bookings',
      color: 'success'
    },
    {
      title: 'Sincronizzazione',
      description: 'Controlla e avvia processi di sincronizzazione',
      icon: <Sync color="info" />,
      path: '/sync',
      color: 'info'
    },
    {
      title: 'Monitoraggio',
      description: 'Visualizza metriche e performance del sistema',
      icon: <Assessment color="warning" />,
      path: '/monitoring',
      color: 'warning'
    },
    {
      title: 'Notifiche',
      description: 'Centro notifiche e alerting del sistema',
      icon: <Notifications color="secondary" />,
      path: '/notifications',
      color: 'secondary'
    },
    {
      title: 'Impostazioni',
      description: 'Configura parametri di sistema e integrazione',
      icon: <Settings color="action" />,
      path: '/settings',
      color: 'primary'
    },
    {
      title: 'Test Endpoints RECUP',
      description: 'Testa tutti gli endpoint RECUP disponibili con i loro parametri e visualizza le risposte JSON',
      icon: <BugReport color="error" />,
      path: '/test-endpoints',
      color: 'error'
    }
  ];

  const systemStatus = {
    healthnet: 'online',
    recup: 'online',
    integration: 'active',
    lastSync: new Date().toLocaleString()
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
      case 'active':
        return 'success';
      case 'offline':
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      default:
        return 'primary';
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          ReCUP Manager Dashboard
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          Sistema di gestione per l'integrazione HealthNET-RECUP Regione Lazio
        </Typography>
      </Box>

      {/* Stato Sistema */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <HealthAndSafety sx={{ mr: 1, verticalAlign: 'middle' }} />
            Stato Sistema
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2">HealthNET:</Typography>
                <Chip
                  label={systemStatus.healthnet}
                  color={getStatusColor(systemStatus.healthnet) as 'success' | 'error' | 'warning' | 'primary'}
                  size="small"
                />
              </Box>
            </Grid>
            <Grid item>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2">RECUP:</Typography>
                <Chip
                  label={systemStatus.recup}
                  color={getStatusColor(systemStatus.recup) as 'success' | 'error' | 'warning' | 'primary'}
                  size="small"
                />
              </Box>
            </Grid>
            <Grid item>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2">Integrazione:</Typography>
                <Chip
                  label={systemStatus.integration}
                  color={getStatusColor(systemStatus.integration) as 'success' | 'error' | 'warning' | 'primary'}
                  size="small"
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm="auto">
              <Typography variant="body2" color="textSecondary">
                Ultima sincronizzazione: {systemStatus.lastSync}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Avviso di benvenuto */}
      <Alert severity="info" sx={{ mb: 4 }}>
        <Typography variant="body2">
          Benvenuto nel sistema ReCUP Manager. Utilizza le sezioni sottostanti per accedere alle diverse funzionalità del sistema.
          Per iniziare, visita la <strong>Dashboard Integrazione</strong> per monitorare lo stato dei servizi.
        </Typography>
      </Alert>

      {/* Cards delle funzionalità */}
      <Grid container spacing={3}>
        {dashboardCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card 
              sx={{ 
                height: '100%',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
              onClick={() => navigate(card.path)}
            >
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box display="flex" alignItems="center" mb={2}>
                  {card.icon}
                  <Typography variant="h6" component="h2" sx={{ ml: 1 }}>
                    {card.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="textSecondary" sx={{ flexGrow: 1, mb: 2 }}>
                  {card.description}
                </Typography>
                <Button
                  variant="outlined"
                  color={card.color as 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(card.path);
                  }}
                >
                  Accedi
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Informazioni aggiuntive */}
      <Box mt={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Informazioni Sistema
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  <strong>Versione:</strong> 1.0.0
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Ambiente:</strong> Produzione
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>NSIS Code:</strong> 202011400
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  <strong>Endpoint RECUP:</strong> gwapi-recup.servicelazio.it
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Endpoint IAM:</strong> iam.regione.lazio.it
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Ultimo aggiornamento:</strong> {new Date().toLocaleDateString()}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default DashboardPage;